"use client";

import { Share2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import * as React from "react";

import PaidPill from "@/components/paid-pill";
import ReferEarn from "@/components/refer-earn";
import { Button } from "@/components/ui/button";
import { Scroll as ScrollSpy } from "@/components/ui/scroll";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface QuickLinksProps {
	className?: string;
	isDosage?: boolean;
	isExamineDatabase?: boolean;
	isResearchFeed?: boolean;
	showPaidPill?: boolean;
	desc?: string;
	page: any;
}

const QuickLinks: React.FC<QuickLinksProps> = ({
	className,
	isDosage,
	isExamineDatabase,
	isResearchFeed,
	showPaidPill,
	desc,
	page,
}) => {
	const pathname = usePathname();

	const searchParams = useSearchParams();

	const { url, type, slug, isSplitTest } = page;
	const searchParamsStr = searchParams.toString();
	const isCurrentPage = pathname === `/${type}/${slug}/`;
	const pagePath = `/${type}/${slug}`;

	const renderLink = ({ id, slug, icon, text, tooltip }) => {
		const content = (
			<Tooltip allowedPlacement={["top", "bottom"]}>
				<TooltipTrigger>
					<div className="m-1.5 inline-flex cursor-pointer items-center space-x-1.5 scroll-smooth rounded bg-examine-purple-700 p-2.5 text-white transition-colors duration-200 hover:bg-examine-purple-200">
						<span className="relative grid size-3.5 shrink-0 place-content-center xl:size-[18px]">
							<Image
								fill={true}
								sizes="(max-width: 768px) 18px, (max-width: 1200px) 18px, 14px"
								src={icon}
								alt={text}
							/>
						</span>
						<span>{text}</span>
					</div>
				</TooltipTrigger>
				<TooltipContent className="[@media(max-width:768px)]:hidden">
					<p>{tooltip}</p>
				</TooltipContent>
			</Tooltip>
		);

		if (isSplitTest) {
			return <Link href={`${pagePath}/${slug}/?${searchParamsStr}`}>{content}</Link>;
		}

		if (!isCurrentPage) {
			return <Link href={`${pagePath}/#${id}`}>{content}</Link>;
		}

		return (
			<ScrollSpy to={id} spy offset={-120} smooth hashSpy duration={0} activeClass="isActive-link">
				{content}
			</ScrollSpy>
		);
	};

	return (
		<div className={cn("grid items-center text-xs md:flex lg:text-sm xl:text-base", className)}>
			<div className="-m-1.5 flex flex-wrap items-center gap-2">
				{isDosage &&
					renderLink({
						slug: "how-to-take",
						id: "dosage-information",
						icon: "/sidebar/dosage_white.png",
						text: "Dosage",
						tooltip: "Evidence-based amounts, timing, and more.",
					})}

				{isExamineDatabase &&
					renderLink({
						slug: "database",
						id: "examine-database",
						icon: "/sidebar/examinedatabase_white.png",
						text: "Examine Database",
						tooltip: "What works and what does not according to human trials.",
					})}

				{isResearchFeed &&
					renderLink({
						slug: "research-feed",
						id: "research-feed",
						icon: "/sidebar/researchfeed_white.png",
						text: "Research feed",
						tooltip: "The latest studies, summarized in plain language by our researchers.",
					})}

				<ReferEarn desc={desc} url={url}>
					<Button className="m-1.5 inline-flex cursor-pointer items-center space-x-1.5 rounded bg-examine-purple-700 p-2.5 transition-colors duration-200 hover:bg-hover">
						<Share2 className="size-3 xl:size-[18px]" />
						<span className="text-xs lg:text-sm xl:text-base">Share</span>
					</Button>
				</ReferEarn>

				{showPaidPill && <PaidPill />}
			</div>
		</div>
	);
};

export default QuickLinks;
